import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../Contexts/AuthContext";
import { orderService } from "../../../Api/Services/orderService";
import { reportService } from "../../../Api/Services/reportService";
import { Order } from "../../../Models/Order";
import { ShippingDetails } from "../../../Models/ShippingDetails";
import { Address } from "../../../Models/Address";
import { OrderStatus } from "../../../Enums/OrderStatus";
import { ReportFormat } from "../../../Enums/ReportFormat";
import { ShippingMethod } from "../../../Enums/ShippingMethod";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { FaChevronLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import ReviewStep from "../../../Components/ReviewStep/ReviewStep";
import OrderSummary from "../../../Components/OrderSummary/OrderSummary";
import ReportFormatModal from "../../../Components/ReportFormatModal/ReportFormatModal";
import Footer from "../../../Components/Footer/Footer";
import ShippingTrackingEventsModal from "../../../Components/ShippingTrackingEventsModal/ShippingTrackingEventsModal";
import { ShippingTrackingResponse } from "../../../Models/ShippingTracking";
import Button from "../../../Components/Button/Button";

const UserOrderDetailsPage = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const { isAdmin, isOperator } = useAuth();
  
  const basePath = isAdmin ? "/admin" : isOperator ? "/operador" : "";

  const [order, setOrder] = useState<Order | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportFormatModalOpen, setIsReportFormatModalOpen] = useState(false);
  const [isTrackingModalOpen, setTrackingModalOpen] = useState(false);
  const [isTrackingLoading, setTrackingLoading] = useState(false);
  const [trackingData, setTrackingData] = useState<ShippingTrackingResponse | null>(null);

  const isCorreiosShipping =
    order?.shippingMethod === ShippingMethod.CorreiosPAC ||
    order?.shippingMethod === ShippingMethod.CorreiosSedex;

  const canTrackShipping =
    !isAdmin &&
    !isOperator &&
    Boolean(shippingDetails?.trackingCode) &&
    !shippingDetails?.isCompanyDelivery &&
    isCorreiosShipping;

  const canGenerateReport =
    (isAdmin || isOperator) &&
    order?.status === OrderStatus.Paid;

  const loadOrder = useCallback(async (id: string) => {
    setIsLoading(true);
    const order = await orderService.get(id);
    if (!order) {
      toast.info("O pedido não foi encontrado!");
      setIsLoading(false);
      return;
    }
    setOrder(order);
    setAddress(order?.address);
    setShippingDetails(order?.shippingDetails);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (id) {
      void loadOrder(id);
    }
  }, [id, loadOrder]);

  const handleGenerateReport = async (format: ReportFormat) => {
    if (!id) {
      return;
    }

    try {
      setIsGeneratingReport(true);
      const report = await reportService.createOrderReport(id, { format });
      toast.success("Relatório gerado. Abrindo download...");
      window.open(report.url, "_blank", "noopener");
    } catch (error) {
      const message =
        (error as any)?.response?.data ??
        "Não foi possível gerar o relatório para este pedido.";
      toast.error(message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleOpenReportModal = () => {
    setIsReportFormatModalOpen(true);
  };

  const handleSelectReportFormat = (format: ReportFormat) => {
    setIsReportFormatModalOpen(false);
    handleGenerateReport(format);
  };

  const handleTrackShipping = async () => {
    if (!id) {
      return;
    }

    try {
      setTrackingLoading(true);
      setTrackingModalOpen(true);
      const data = await orderService.getShippingTracking(id);
      setTrackingData(data);
    } catch (error) {
      const message = "Não foi possível consultar o rastreio do pedido.";
      toast.error(message);
      setTrackingData({
        trackingCode: "",
        events: [],
        message
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="w-full page-gutter py-4 sm:py-14 bg-gray-50">
      <SkeletonBlock height={28} width={220} />
      <div className="flex flex-col gap-9 mt-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 space-y-4">
            <SkeletonBlock height={60} />
            <SkeletonBlock height={200} />
            <SkeletonBlock height={60} />
          </div>
          <div className="flex flex-col gap-4 flex-[0.5]">
            <SkeletonBlock height={200} />
            <SkeletonBlock height={48} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SmartNavbar />
      {isLoading ? (
        renderSkeleton()
      ) : (
        <div className="w-full page-gutter py-4 sm:py-14 bg-gray-50">
          <PageTitle className="my-6 sm:my-0 sm:mb-8">{"MEUS PEDIDOS"}</PageTitle>
          <div className="flex flex-col gap-9">
            <div className="flex flex-col md:flex-row gap-4">
              <ReviewStep
                selectedAddress={`${address?.street}, ${address?.number} - ${address?.neighborhood} - ${address?.city}/${address?.state} - CEP: ${address?.postalCode}`}
                selectedShipping={shippingDetails ? {
                  methodType: order?.shippingMethod!,
                  price: shippingDetails.price ?? null,
                  deliveryTime: shippingDetails.estimatedTimeInDays ?? null
                } : null}
                isOrderPage={true}
                isAdmin={isAdmin}
                isOperator={isOperator}
                order={order}
              />

              <div className="flex flex-col gap-4 flex-[0.5]">
                <OrderSummary
                  originalTotalPrice={order?.originalTotalPrice}
                  orderProductDiscount={order?.productDiscount}
                  orderCouponDiscount={order?.couponDiscount}
                  orderShippingPrice={shippingDetails?.price}
                  orderShippingMethod={order?.shippingMethod ?? null}
                  orderBankingBilletFee={order?.totalBankingBilletFee ?? order?.bankingBilletFee ?? null}
                  orderBaseBankingBilletFee={order?.bankingBilletFee ?? null}
                  orderInterest={order?.creditCardInterest ?? null}
                  orderFinalPrice={order?.finalPrice}
                />
                <div className="bg-white border rounded shadow p-4 flex flex-col gap-3">
                  <Button variant="secondary"
                    className="flex items-center justify-center gap-1 text-sm w-full py-2"
                    onClick={() => navigate(isAdmin || isOperator ? `${basePath}/pedidos` : toPublicPath("/pedidos"))}
                  >
                    <FaChevronLeft />
                    {"VOLTAR AOS MEUS PEDIDOS"}
                  </Button>

                  {canTrackShipping && (
                    <Button
                      className="flex items-center justify-center gap-1 text-sm w-full py-2"
                      onClick={handleTrackShipping}
                      disabled={isTrackingLoading}
                    >
                      {isTrackingLoading ? "CONSULTANDO RASTREIO..." : "RASTREAR PEDIDO"}
                    </Button>
                  )}

                  {canGenerateReport && (
                    <Button isLoading={isGeneratingReport}
                      className="flex items-center justify-center gap-1 text-sm w-full py-2"
                      onClick={handleOpenReportModal}
                      disabled={isGeneratingReport}
                    >
                      {"GERAR RELATÓRIO DO PEDIDO"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ReportFormatModal
        isOpen={isReportFormatModalOpen}
        onClose={() => setIsReportFormatModalOpen(false)}
        onSelect={handleSelectReportFormat}
      />
      <ShippingTrackingEventsModal
        isOpen={isTrackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        tracking={trackingData}
        isLoading={isTrackingLoading}
      />
      <Footer />
    </div>
  );
};

export default UserOrderDetailsPage;
